/**
 * CSRF cookie access.
 *
 * ADR 0001 splits the session into three `__Host-` cookies:
 *
 * | cookie                | carries            | HttpOnly |
 * | --------------------- | ------------------ | -------- |
 * | `__Host-ols_session`  | session token      | yes      |
 * | `__Host-ols_key`      | envelope unlock key| yes      |
 * | `__Host-ols_csrf`     | double-submit token| **no**   |
 *
 * Only the third is readable here, and that is the entire point: the frontend
 * never holds session material, it only echoes the CSRF token back in a header
 * so the backend can prove the request came from our own document.
 *
 * ## Why `document.cookie` and not `cookieStore`
 *
 * `cookieStore.get()` is a promise. The header has to be attached
 * *synchronously* while building the `RequestInit`, and awaiting a cookie read
 * before every mutation adds a microtask hop and a Safari fallback for no gain:
 * `document.cookie` is the synchronous source for exactly the non-HttpOnly
 * cookie we need. `cookieStore` matters in the service worker, which has no
 * `document` — that is §10's problem, not this module's.
 *
 * The cookie is read **on every request**, never cached at boot: the backend
 * re-emits it on login and on password change (rotation), and a cached value
 * would go stale precisely when a mutation matters.
 */

/** Header the backend expects the double-submit token in (ADR 0001). */
export const CSRF_HEADER = 'X-CSRF-Token';

/**
 * Cookie names tried in order.
 *
 * `__Host-` requires `Secure`, which requires HTTPS. Production and the mkcert
 * dev proxy both have it; a plain-HTTP `vite dev` against a backend that
 * degrades the prefix does not, so the unprefixed name is accepted as a
 * development fallback. It is a fallback, not an alternative: the prefixed
 * cookie always wins when both are present.
 */
export const CSRF_COOKIE_NAMES = ['__Host-ols_csrf', 'ols_csrf'] as const;

/** Methods that never carry a CSRF token, because they never change state. */
const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/** True when a request with this method must double-submit the CSRF token. */
export function isStateChangingMethod(method: string): boolean {
  return !SAFE_METHODS.has(method.toUpperCase());
}

/**
 * Reads one cookie by exact name.
 *
 * Deliberately not a regex built from the name: `__Host-ols_csrf` contains no
 * metacharacters today, but a scattered hand-rolled regex is how a cookie
 * called `xols_csrf` starts matching. Split, trim, compare.
 */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const jar = document.cookie;
  if (!jar) return null;
  for (const part of jar.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    const value = part.slice(eq + 1).trim();
    return value === '' ? null : decodeURIComponent(value);
  }
  return null;
}

/**
 * The current CSRF token, or `null` when the cookie is absent.
 *
 * Absent is a normal state, not an error: before the first login, after the
 * backend cleared the trio, and in any tab whose cookie was rotated away. The
 * transport handles it by letting the request go out without the header and
 * treating the resulting `403 csrf_invalid` as a re-prime signal, rather than
 * by guessing a token or blocking the call.
 */
export function readCsrfToken(): string | null {
  for (const name of CSRF_COOKIE_NAMES) {
    const value = readCookie(name);
    if (value !== null) return value;
  }
  return null;
}
